import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserProductDuplicatedModel, GeneratorProductModel, GeneratorProductLogisticModel } from '@/Models';
import { CountryCode, InternationalType } from '@/Enums';
import { Nullable } from '@/Types';
import { asyncActions } from './asyncActions';
import { GeneratorProductStatusesModel } from '@/Models/GeneratorProducts/GeneratorProductStatusesModel';

function shouldUpdateProduct(product: GeneratorProductModel, model: GeneratorProductModel, selectedProductId?: number) {
  if (selectedProductId !== undefined) {
    return product.id === selectedProductId;
  }
  return product.id === model.id;
}

const updateGeneratorProduct = (
  templates: GeneratorProductLogisticModel[],
  model: GeneratorProductModel,
  selectedTemplateId?: number,
) => {
  const templateLogistic = templates.find((x) => x.logisticId == model.logisticTypeId);

  if (templateLogistic) {
    templateLogistic.templates = templateLogistic.templates.map((product) => {
      return shouldUpdateProduct(product, model, selectedTemplateId) ? model : product;
    });
  }
};

export type GeneratorProductState = {
  productStatuses: GeneratorProductStatusesModel[];
  selectedCountry: Nullable<CountryCode>;
  selectedLogistic: Nullable<string | number>;
  templates: GeneratorProductLogisticModel[];
  isCreatingNewTemplate: boolean;
  needProductDuplicatedModal: boolean;
  duplicatedProducts: UserProductDuplicatedModel[];
};

const initialState: GeneratorProductState = {
  productStatuses: [],
  selectedCountry: null,
  selectedLogistic: null,
  templates: [],
  isCreatingNewTemplate: false,
  needProductDuplicatedModal: false,
  duplicatedProducts: [],
};

const generationProductsSlice = createSlice({
  name: 'GeneratorProductsState',
  initialState,
  reducers: {
    setSelectedCountry(state, action: PayloadAction<Nullable<CountryCode>>) {
      state.selectedCountry = action.payload;
    },
    setSelectedLogistic(state, action: PayloadAction<Nullable<string | number>>) {
      state.selectedLogistic = action.payload;
    },
    setIsCreatingNewTemplate(state) {
      state.isCreatingNewTemplate = true;
    },
    closeProductDuplicatedModal(state) {
      state.needProductDuplicatedModal = false;
    },
    clearData(state) {
      state.templates = [];
      state.needProductDuplicatedModal = false;
      state.isCreatingNewTemplate = false;
      state.selectedCountry = null;
      state.selectedLogistic = null;
    },
    addGeneratorProduct(state, action: PayloadAction<GeneratorProductModel>) {
      const { countryCode, logisticTypeId } = action.payload;
      let templateLogistic = state.templates.find((x) => x.logisticId == logisticTypeId);

      if (templateLogistic) {
        templateLogistic.templates.push(action.payload);
      } else {
        templateLogistic = {
          country: countryCode,
          logisticId: Number(logisticTypeId),
          templates: [action.payload],
        };
        state.templates.push(templateLogistic);
      }
    },
    deleteGeneratorProduct(state, action: PayloadAction<GeneratorProductModel>) {
      const { uuid, logisticTypeId } = action.payload;
      const templateLogistic = state.templates.find((x) => x.logisticId == logisticTypeId);

      if (templateLogistic) {
        templateLogistic.templates = templateLogistic.templates.filter((x) => x.uuid != uuid);

        if (templateLogistic.templates.length === 0) {
          state.templates = state.templates.filter((x) => x.country != templateLogistic.country);
        }
      }
    },
    resetToDomesticGeneratorProduct(state, action: PayloadAction<GeneratorProductModel>) {
      const { uuid, logisticTypeId } = action.payload;
      const templateLogistic = state.templates.find((x) => x.logisticId == logisticTypeId);

      if (templateLogistic) {
        const template = templateLogistic.templates.find((x) => x.uuid === uuid);
        if (template) {
          template.internationalType = InternationalType.Domestic;
          template.countryZoneRecepientId = '';
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(asyncActions.getProductStatusesRequest.fulfilled, (state, action) => {
        state.productStatuses = action.payload.statuses;
      })
      .addCase(asyncActions.getGeneratorProductsRequest.fulfilled, (state, action) => {
        state.templates = action.payload.templates?.[0]?.logistics || [];
      })
      .addCase(asyncActions.createGeneratorProductRequest.fulfilled, (state, action) => {
        updateGeneratorProduct(state.templates, action.payload.template, 0);
        state.isCreatingNewTemplate = false;
      })
      .addCase(asyncActions.updateGeneratorProductRequest.fulfilled, (state, action) => {
        updateGeneratorProduct(state.templates, action.payload.template);
      })
      .addCase(asyncActions.deleteGeneratorProductRequest.fulfilled, (state, action) => {
        state.templates.forEach((y) => {
          y.templates = y.templates.filter((z) => z.id !== action.payload);
        });
        state.templates = state.templates.filter((y) => y.templates.length !== 0);
      })
      .addCase(asyncActions.generateProductsRequest.fulfilled, (state, action) => {
        const { template, duplicatedProducts } = action.payload;

        if (duplicatedProducts.length > 0) {
          state.duplicatedProducts = duplicatedProducts;
          state.needProductDuplicatedModal = true;
        } else {
          state.duplicatedProducts = [];
        }

        updateGeneratorProduct(state.templates, template);
      });
  },
});

const { actions, reducer } = generationProductsSlice;

export const generatorProductsActions = actions;
export const generatorProductsReducer = reducer;
export const generatorProductsAsyncActions = asyncActions;
